import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { OnboardingDirective } from '@lassi/components';
import { FeatureFlagDirective, IFeatureFlagKeys } from '@lassi/config';
import {
  AdminRoles,
  AuthService,
  RouteUtilsService,
  SmesRequestsService,
} from '@lassi/webapp-shared';

@Component({
  selector: 'lassi-header',
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatDivider,
    RouterModule,
    FeatureFlagDirective,
    OnboardingDirective,
    MatBadgeModule,
  ],
  template: `
    <mat-toolbar class="header-toolbar">
      <div class="title-container">
        <a href="#" aria-label="Home page">
          <img
            src="assets/logos/vicgov-logos/Vic-Logo-small.svg"
            alt="LASSI Logo"
            class="logo desktop-logo"
          />
          <img
            src="assets/logos/vicgov-logos/Vic-Logo-mini.svg"
            alt="LASSI Logo"
            class="logo mobile-logo"
          />
        </a>
        <a href="#">
          <div class="text-container">
            <span class="title mat-title">LASSI </span>
            <span class="long-title">Land and Survey Spatial Information</span>
          </div>
        </a>
      </div>
      <span class="spacer"></span>

      <!-- in maintenance mode, will not show the following -->
      @if (!isMaintenanceMode()) {
        <!-- button group in non-mobile view -->
        <div class="button-container">
          <a
            mat-button
            [routerLink]="['/help']"
            [queryParams]="{
              redirectTo: route(),
            }"
            class="help-menu-button"
            lassiOnboardingAnchor="tour.help"
          >
            <span class="button-content">
              <span>Help</span>
              <mat-icon fontSet="material-symbols-outlined"
                >help_outline</mat-icon
              >
            </span>
          </a>
          @if (!isAuthenticated()) {
            <a
              lassiOnboardingAnchor="tour.auth"
              mat-stroked-button
              class="login-button"
              size="small"
              [routerLink]="['login']"
              [queryParams]="{
                redirectTo: route(),
              }"
            >
              Log in
            </a>
            <a
              mat-flat-button
              size="small"
              [routerLink]="['register']"
              [queryParams]="{
                redirectTo: route(),
              }"
            >
              Register
            </a>
          } @else {
            @defer {
              <button
                mat-fab
                size="small"
                class="profile-button"
                [matMenuTriggerFor]="profileMenu"
                #profileMenuTrigger="matMenuTrigger"
                [class.active]="profileMenuTrigger.menuOpen"
                lassiOnboardingAnchor="tour.auth"
                aria-label="Profile"
                [matBadge]="numSmesRequests() || undefined"
              >
                <mat-icon fontSet="material-symbols-outlined"> person</mat-icon>
              </button>
            }
          }
        </div>

        <!-- Stack button in mobile view -->
        <button
          class="menu-button"
          mat-icon-button
          [matMenuTriggerFor]="appMenu"
          #menuTrigger="matMenuTrigger"
          [class.active]="menuTrigger.menuOpen"
          aria-label="Menu"
        >
          <mat-icon fontSet="material-symbols-outlined">Menu</mat-icon>
        </button>
      }

      <!-- Menu for hamburger menu in mobile view -->
      <mat-menu #appMenu="matMenu">
        <a
          mat-menu-item
          [routerLink]="['/help']"
          [queryParams]="{
            redirectTo: route(),
          }"
        >
          <mat-icon fontSet="material-symbols-outlined">help_outline</mat-icon>
          <span>Help</span>
        </a>
        @if (isAuthenticated()) {
          <mat-divider></mat-divider>
          <a
            mat-menu-item
            [routerLink]="['/profile']"
            [queryParams]="{
              redirectTo: route(),
            }"
          >
            <mat-icon> person </mat-icon>
            <span>Profile</span>
          </a>
          <button mat-menu-item (click)="handleLogout()">
            <mat-icon> logout </mat-icon>
            <span>Logout</span>
          </button>
        } @else {
          <mat-divider></mat-divider>
          <a
            mat-menu-item
            [routerLink]="['login']"
            [queryParams]="{
              redirectTo: route(),
            }"
          >
            <span>Log in</span>
          </a>
          <a
            mat-menu-item
            [routerLink]="['register']"
            [queryParams]="{
              redirectTo: route(),
            }"
          >
            <span>Register</span>
          </a>
        }
      </mat-menu>

      <mat-menu #profileMenu="matMenu">
        <a
          mat-menu-item
          [routerLink]="['/profile']"
          [queryParams]="{
            redirectTo: route(),
          }"
        >
          <mat-icon>person</mat-icon>
          Profile
        </a>
        @if (isAdmin()) {
          <a
            *featureFlag="IFeatureFlagKeys.enableAdminDashboard"
            mat-menu-item
            [routerLink]="['/admin']"
            [queryParams]="{
              redirectTo: route(),
            }"
          >
            <mat-icon [matBadge]="numSmesRequests() || undefined">
              groups
            </mat-icon>
            <span>Admin Dashboard</span>
          </a>
        }
        <button mat-menu-item (click)="handleLogout()">
          <mat-icon> logout </mat-icon>
          Logout
        </button>
      </mat-menu>
    </mat-toolbar>
  `,
  styles: `
    @use 'breakpoints' as *;
    @use '@angular/material' as mat;
    @use 'theme.scss' as theme;

    mat-toolbar {
      height: 72px;

      .spacer {
        flex: 1 1 auto;
      }
    }

    mat-toolbar.header-toolbar {
      padding: 0 32px;
    }

    .title-container {
      display: flex;
      align-items: center;
      gap: 24px;

      .logo {
        height: 40px;
        width: auto;
        vertical-align: middle;

        &.mobile-logo {
          display: none;
        }
      }

      @include handset {
        .logo {
          &.desktop-logo {
            display: none;
          }

          &.mobile-logo {
            display: inline-block;
          }
        }
      }

      .text-container {
        font-size: 16px;
      }

      a {
        color: inherit;
        text-decoration: none;
      }
    }

    .title {
      font-weight: bold;
      margin-right: 0.25rem;
    }

    .menu-button {
      color: inherit;

      @include web {
        display: none;
      }

      @include mat.menu-color(theme.$light-theme);

      &:hover {
        background-color: #{mat.get-theme-color(
            theme.$light-theme,
            secondary,
            35
          )};
      }

      &.active {
        color: black;
        background-color: #{mat.get-theme-color(
            theme.$light-theme,
            secondary,
            90
          )};
      }
    }

    .login-button {
      margin-right: 0.5rem;
    }

    .button-container {
      display: none;

      @include web {
        display: flex;
        align-items: center;
        gap: 24px;

        button {
          height: 40px;
        }
      }
    }

    .long-title {
      display: none;

      @include tablet {
        display: initial;
      }
    }

    .help-menu-button {
      .button-content {
        display: flex;
        align-items: center;
        flex-direction: row;
      }

      .mat-icon {
        margin-left: 8px;
      }
    }

    .profile-button {
      width: 40px;
      border-radius: 50%;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly authService = inject(AuthService);
  readonly numSmesRequests = inject(SmesRequestsService).numSmesRequests;
  route = inject(RouteUtilsService).route;

  protected isMaintenanceMode = signal<boolean>(false);
  protected isAuthenticated = this.authService.isLoggedIn;
  protected isAdmin = toSignal(this.authService.canActivate(AdminRoles));

  readonly IFeatureFlagKeys = IFeatureFlagKeys;

  handleLogout() {
    this.authService.signout();
  }
}
